import { EngagementMessage } from "./components/EngagementMessage/EngagementMessage";
import { InboxMessageAge } from "./components/InboxMessageAge/InboxMessageAge";
import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import type { ApiInboxMessage } from "@bridge-applications/pb-types";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import beachCoinIcon from "../../../../../assets/static/images/coins/style1/coin-style1__beach-coin.webp";
import gramIcon from "../../../../../assets/static/images/coins/style1/coin-style1__gram.webp";
import notcoinIcon from "../../../../../assets/static/images/coins/style1/coin-style1__not.webp";
import usdtIcon from "../../../../../assets/static/images/coins/style1/coin-style1__usdt.webp";
import { formatCompactCurrencyMinorUnits } from "../../../../../logic/helpers/currencyAmount";
import type { InboxController } from "../../../../../logic/hooks/useInbox";
import type { SpinTogetherController } from "../../../../../logic/hooks/useSpinTogether";
import {
    ButtonVar1,
    type ButtonVar1Colors,
} from "../../../../common/ButtonVar1/ButtonVar1";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import { RewardCollectionDialog } from "../../../../common/RewardCollectionDialog/RewardCollectionDialog";
import { useInboxRewardCollection } from "../../../../../logic/hooks/useInboxRewardCollection";
import "./InboxDialog.scss";

const INBOX_MESSAGE_COLORS: ButtonVar1Colors = {
    containerStroke: "#000000",
    containerFill: `linear-gradient(
        to bottom,
        #854a00 0%,
        #854a00 87%,
        #ba6700 100%
    )`,
    faceFill: "linear-gradient(to bottom, #FFE28B 0%, #FFE28B 100%)",
    faceStroke: `linear-gradient(
        to bottom,
        #ffecac 0%,
        #fff4ce 39%,
        #ffecaa 58%,
        #fff0bc 100%
    )`,
};

const CURRENCY_ICONS: Readonly<Record<string, string>> = {
    BEACH_COINS: beachCoinIcon,
    GRAM: gramIcon,
    NOT: notcoinIcon,
    USDT: usdtIcon,
};

const READ_VIEW_THRESHOLD = 0.6;
const READ_VIEW_DURATION_MS = 500;
const READ_BATCH_DELAY_MS = 75;

interface InboxDialogProps {
    inbox: InboxController;
    isOpen: boolean;
    onClose: () => void;
    onRequestFriendSteal: () => void;
    spinTogether: Pick<SpinTogetherController, "respondToInvite">;
}

interface InboxActorAvatarProps {
    actor: ApiInboxMessage["actor"];
}

interface InboxMessageViewProps {
    children: ReactNode;
    isMarking: boolean;
    isOpen: boolean;
    isUnread: boolean;
    messageId: string;
    onViewed: (messageId: string) => void;
}

function actorInitials(actor: ApiInboxMessage["actor"]): string {
    const namedInitials = [actor.firstName, actor.lastName]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.slice(0, 1))
        .join("");
    if (namedInitials) return namedInitials.toUpperCase();

    return actor.displayName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.slice(0, 1))
        .join("")
        .toUpperCase();
}

function InboxActorAvatar({ actor }: InboxActorAvatarProps) {
    return (
        <span className="inbox-dialog__avatar" aria-hidden="true">
            <span className="inbox-dialog__avatar-initials">
                {actorInitials(actor)}
            </span>
            {actor.avatarUrl && (
                <img
                    className="inbox-dialog__avatar-photo"
                    src={actor.avatarUrl}
                    alt=""
                    draggable={false}
                    loading="lazy"
                    onError={(event) => {
                        event.currentTarget.style.display = "none";
                    }}
                />
            )}
        </span>
    );
}

function InboxMessageView({
    children,
    isMarking,
    isOpen,
    isUnread,
    messageId,
    onViewed,
}: InboxMessageViewProps) {
    const itemRef = useRef<HTMLLIElement>(null);
    const hasReportedViewRef = useRef(false);

    useEffect(() => {
        const item = itemRef.current;
        if (!isOpen || !isUnread) {
            hasReportedViewRef.current = false;
            return;
        }
        if (!item || isMarking) return;

        let dwellTimer: number | null = null;
        let isMeaningfullyVisible = false;
        let observer: IntersectionObserver | null = null;

        const clearDwellTimer = () => {
            if (dwellTimer === null) return;
            window.clearTimeout(dwellTimer);
            dwellTimer = null;
        };

        const syncDwellTimer = () => {
            const canCountAsViewed =
                isMeaningfullyVisible &&
                document.visibilityState === "visible" &&
                !hasReportedViewRef.current;

            if (!canCountAsViewed) {
                clearDwellTimer();
                return;
            }
            if (dwellTimer !== null) return;

            dwellTimer = window.setTimeout(() => {
                dwellTimer = null;
                hasReportedViewRef.current = true;
                onViewed(messageId);
            }, READ_VIEW_DURATION_MS);
        };

        const handleVisibilityChange = () => {
            syncDwellTimer();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        if ("IntersectionObserver" in window) {
            observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.target !== item) continue;
                        isMeaningfullyVisible =
                            entry.isIntersecting &&
                            entry.intersectionRatio >= READ_VIEW_THRESHOLD;
                        syncDwellTimer();
                    }
                },
                { threshold: [READ_VIEW_THRESHOLD] },
            );
            observer.observe(item);
        } else {
            isMeaningfullyVisible = true;
            syncDwellTimer();
        }

        return () => {
            clearDwellTimer();
            observer?.disconnect();
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
        };
    }, [isMarking, isOpen, isUnread, messageId, onViewed]);

    return <li ref={itemRef}>{children}</li>;
}

export function InboxDialog({
    inbox,
    isOpen,
    onClose,
    onRequestFriendSteal,
    spinTogether,
}: InboxDialogProps) {
    const { t, i18n } = useTranslation();
    const [markingIds, setMarkingIds] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const [deferredReadStyleIds, setDeferredReadStyleIds] = useState<
        ReadonlySet<string>
    >(() => new Set());
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [respondingInviteId, setRespondingInviteId] = useState<string | null>(
        null,
    );
    const markingIdsRef = useRef(new Set<string>());
    const queuedReadIdsRef = useRef(new Set<string>());
    const readBatchTimerRef = useRef<number | null>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const rewardCollection = useInboxRewardCollection({
        isOpen,
        claim: inbox.claim,
        scrollerRef: bodyRef,
    });
    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const markInboxRead = inbox.markRead;

    useLayoutEffect(() => {
        // Run after SimpleDialog opens/focuses, before the first visible paint.
        // Refreshes and pagination must preserve the reader's current position.
        if (isOpen) bodyRef.current?.scrollTo({ top: 0, behavior: "instant" });
    }, [isOpen]);

    const deferReadStyle = useCallback((messageId: string) => {
        setDeferredReadStyleIds((currentMessageIds) => {
            if (currentMessageIds.has(messageId)) return currentMessageIds;

            const nextMessageIds = new Set(currentMessageIds);
            nextMessageIds.add(messageId);
            return nextMessageIds;
        });
    }, []);

    const markRead = useCallback(
        async (messageIds: readonly string[]) => {
            const pendingMessageIds = [...new Set(messageIds)].filter(
                (messageId) => !markingIdsRef.current.has(messageId),
            );
            if (pendingMessageIds.length === 0) return;

            for (const messageId of pendingMessageIds) {
                markingIdsRef.current.add(messageId);
            }
            setMarkingIds(new Set(markingIdsRef.current));
            setMutationError(null);

            try {
                await markInboxRead(pendingMessageIds);
            } catch (error) {
                setMutationError(
                    error instanceof Error ? error.message : String(error),
                );
            } finally {
                for (const messageId of pendingMessageIds) {
                    markingIdsRef.current.delete(messageId);
                }
                setMarkingIds(new Set(markingIdsRef.current));
            }
        },
        [markInboxRead],
    );

    const flushQueuedReads = useCallback(() => {
        readBatchTimerRef.current = null;
        const messageIds = [...queuedReadIdsRef.current];
        queuedReadIdsRef.current.clear();
        void markRead(messageIds);
    }, [markRead]);

    const queueRead = useCallback(
        (messageId: string) => {
            deferReadStyle(messageId);
            queuedReadIdsRef.current.add(messageId);
            if (readBatchTimerRef.current !== null) return;

            readBatchTimerRef.current = window.setTimeout(
                flushQueuedReads,
                READ_BATCH_DELAY_MS,
            );
        },
        [deferReadStyle, flushQueuedReads],
    );

    useEffect(() => {
        if (isOpen) return;

        setDeferredReadStyleIds((currentMessageIds) =>
            currentMessageIds.size === 0 ? currentMessageIds : new Set(),
        );
    }, [isOpen]);

    useEffect(
        () => () => {
            if (readBatchTimerRef.current !== null) {
                window.clearTimeout(readBatchTimerRef.current);
            }
        },
        [],
    );

    const openFriendSteal = (
        message: Extract<ApiInboxMessage, { kind: "FRIEND_STEAL_RECEIVED" }>,
    ) => {
        if (message.readAt === null && !markingIdsRef.current.has(message.id)) {
            deferReadStyle(message.id);
            queuedReadIdsRef.current.delete(message.id);
            void markRead([message.id]);
        }
        onRequestFriendSteal();
    };

    const respondToInvite = async (
        message: Extract<ApiInboxMessage, { kind: "SPIN_TOGETHER_INVITE" }>,
        response: "ACCEPT" | "DECLINE",
    ) => {
        setRespondingInviteId(message.inviteId);
        setMutationError(null);
        try {
            await spinTogether.respondToInvite(message.inviteId, response);
            if (
                message.readAt === null &&
                !markingIdsRef.current.has(message.id)
            ) {
                await markRead([message.id]);
            }
            await inbox.refresh();
        } catch (error) {
            setMutationError(
                error instanceof Error ? error.message : String(error),
            );
        } finally {
            setRespondingInviteId(null);
        }
    };

    const inboxDialog = (
        <SimpleDialog
            className="inbox-dialog"
            isOpen={isOpen}
            onClose={onClose}
            initialFocusRef={titleRef}
            aria-label={t("inbox.title", {
                count: inbox.unreadCount,
            })}
        >
            <div ref={bodyRef} className="inbox-dialog__body" tabIndex={-1}>
                <h2 ref={titleRef} className="inbox-dialog__title" tabIndex={-1}>
                    {t("mainPage.inbox.label")}
                </h2>
                <div className="inbox-dialog__messages-container">
                    {inbox.status === "loading" &&
                        inbox.messages.length === 0 && (
                            <p className="inbox-dialog__state">
                                {t("inbox.loading")}
                            </p>
                        )}
                    {(inbox.error || mutationError) && (
                        <p className="inbox-dialog__state" role="alert">
                            {inbox.error ?? mutationError}
                        </p>
                    )}
                    {inbox.status === "ready" &&
                        inbox.messages.length === 0 && (
                            <p className="inbox-dialog__state">
                                {t("inbox.empty")}
                            </p>
                        )}
                    <ul className="inbox-dialog__messages">
                        {inbox.messages.map((message) => (
                            <InboxMessageView
                                key={message.id}
                                isMarking={markingIds.has(message.id)}
                                isOpen={isOpen}
                                isUnread={message.readAt === null}
                                messageId={message.id}
                                onViewed={queueRead}
                            >
                                {message.kind === "ENGAGEMENT" ? (
                                    <EngagementMessage
                                        message={message}
                                        onClaim={rewardCollection.claimAndPresent}
                                    />
                                ) : message.kind === "FRIEND_STEAL_RECEIVED" ? (
                                    <ButtonVar1
                                    className="inbox-dialog__message"
                                    colors={INBOX_MESSAGE_COLORS}
                                    data-read={String(
                                        message.readAt !== null &&
                                            !deferredReadStyleIds.has(
                                                message.id,
                                            ),
                                    )}
                                    aria-busy={markingIds.has(message.id)}
                                    aria-label={t(
                                        "inbox.friendStealActionLabel",
                                        {
                                            name: message.actor.displayName,
                                        },
                                    )}
                                    onClick={() => openFriendSteal(message)}
                                    content={
                                        <div className="inbox-dialog__message-layout">
                                            <div className="inbox-dialog__message-left">
                                                <InboxActorAvatar
                                                    actor={message.actor}
                                                />
                                            </div>
                                            <div className="inbox-dialog__message-right">
                                                <InboxMessageAge
                                                    createdAt={message.createdAt}
                                                />
                                                <p className="inbox-dialog__message-title">
                                                    <Trans
                                                        i18nKey="inbox.friendStealReceived"
                                                        values={{
                                                            name: message.actor
                                                                .displayName,
                                                        }}
                                                        components={{
                                                            highlight: (
                                                                <strong />
                                                            ),
                                                        }}
                                                    />
                                                </p>
                                                <span
                                                    className="inbox-dialog__message-divider"
                                                    aria-hidden="true"
                                                />
                                                <div
                                                    className="inbox-dialog__message-transfers"
                                                    dir="ltr"
                                                >
                                                    {message.transfers.map(
                                                        (transfer) => {
                                                            const icon =
                                                                CURRENCY_ICONS[
                                                                    transfer
                                                                        .currencyCode
                                                                ];
                                                            const amount =
                                                                formatCompactCurrencyMinorUnits(
                                                                    transfer.amount,
                                                                    transfer.currencyCode,
                                                                    locale,
                                                                );

                                                            return (
                                                                <span
                                                                    className="inbox-dialog__message-transfer"
                                                                    key={
                                                                        transfer.currencyCode
                                                                    }
                                                                    aria-label={t(
                                                                        "inbox.stolenTransfer",
                                                                        {
                                                                            amount,
                                                                            currency:
                                                                                transfer.currencyCode,
                                                                        },
                                                                    )}
                                                                >
                                                                    {icon ? (
                                                                        <img
                                                                            className="inbox-dialog__message-currency"
                                                                            src={
                                                                                icon
                                                                            }
                                                                            alt=""
                                                                            aria-hidden="true"
                                                                            draggable={
                                                                                false
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <span className="inbox-dialog__message-currency-code">
                                                                            {
                                                                                transfer.currencyCode
                                                                            }
                                                                        </span>
                                                                    )}
                                                                    <span className="inbox-dialog__message-amount">
                                                                        −
                                                                        {amount}
                                                                    </span>
                                                                </span>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                                <span className="inbox-dialog__message-revenge">
                                                    {t("inbox.takeRevenge")}
                                                </span>
                                            </div>
                                        </div>
                                    }
                                    />
                                ) : (
                                    <article
                                        className="inbox-dialog__spin-invite"
                                        data-read={String(
                                            message.readAt !== null &&
                                                !deferredReadStyleIds.has(
                                                    message.id,
                                                ),
                                        )}
                                    >
                                        <InboxActorAvatar
                                            actor={message.actor}
                                        />
                                        <div>
                                            <p>
                                                {t(
                                                    "spinTogether.inboxInvite",
                                                    {
                                                        name: message.actor
                                                            .displayName,
                                                        defaultValue:
                                                            "{{name}} wants to play together now.",
                                                    },
                                                )}
                                            </p>
                                            {message.inviteState ===
                                            "PENDING" ? (
                                                <div className="inbox-dialog__spin-invite-actions">
                                                    <SoundButton
                                                        type="button"
                                                        disabled={
                                                            respondingInviteId ===
                                                            message.inviteId
                                                        }
                                                        onClick={() =>
                                                            void respondToInvite(
                                                                message,
                                                                "ACCEPT",
                                                            )
                                                        }
                                                    >
                                                        {t(
                                                            "spinTogether.accept",
                                                            {
                                                                defaultValue:
                                                                    "Accept",
                                                            },
                                                        )}
                                                    </SoundButton>
                                                    <SoundButton
                                                        type="button"
                                                        disabled={
                                                            respondingInviteId ===
                                                            message.inviteId
                                                        }
                                                        onClick={() =>
                                                            void respondToInvite(
                                                                message,
                                                                "DECLINE",
                                                            )
                                                        }
                                                    >
                                                        {t(
                                                            "spinTogether.decline",
                                                            {
                                                                defaultValue:
                                                                    "Decline",
                                                            },
                                                        )}
                                                    </SoundButton>
                                                </div>
                                            ) : (
                                                <span>
                                                    {message.inviteState}
                                                </span>
                                            )}
                                        </div>
                                    </article>
                                )}
                            </InboxMessageView>
                        ))}
                    </ul>
                    {inbox.nextCursor && (
                        <SoundButton
                            type="button"
                            className="inbox-dialog__load-more"
                            disabled={inbox.status === "loading"}
                            onClick={() => void inbox.loadMore()}
                        >
                            {t("inbox.loadMore")}
                        </SoundButton>
                    )}
                </div>
            </div>
        </SimpleDialog>
    );

    return (
        <>
            {inboxDialog}
            <RewardCollectionDialog
                presentation={rewardCollection.presentation}
                onCollect={rewardCollection.fadeSound}
                onComplete={rewardCollection.complete}
            />
        </>
    );
}
